import { types as t } from 'mobx-state-tree';
import ModelInformation, { type IModelInformation } from './model_information';
import type { IModelVersion } from './model_version';
import { DateTime } from 'luxon';

type SaveState = 'idle' | 'error' | 'busy';

/**
 * Hand-declared instance interface for Project. Project sits on the projects
 * recursion boundary (its views reach into ModelInformation / ModelVersion, which
 * reach back via `getParent`), so per the MST typing pattern (§3) we declare the
 * interface by hand — referencing the *interfaces* of the related models lazily,
 * never their concrete `typeof` — and use it to rebind `self` inside the views
 * block (§4).
 */
export interface IProject {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly models: IModelInformation[];
  readonly displayName: string;
  readonly model: IModelInformation | null;
  readonly activeModelVersion: IModelVersion | null | undefined;
  readonly updated: DateTime;
  readonly lastEdited: DateTime;
  readonly lastActivity: DateTime;
  readonly saveState: SaveState;
  readonly path: string;
  retrySave(): Promise<unknown>;
}

/**
 * A Project (see `ARCHITECTURE.md`)
 */
const Project = t
  .model('Project', {
    id: t.identifier,
    name: t.string,
    createdAt: t.string,
    updatedAt: t.string,
    // the backend already supports a list of models in a project, so we store
    // them in an array here too. See also the convenience `get model()` below.
    models: t.array(ModelInformation),
  })
  .views(self0 => {
    // Rebind `self` to the hand-declared interface so the sibling views below
    // (`self.model`, `self.updated`, `self.lastEdited`, …) type — same observable
    // node at runtime (§4).
    const self = self0 as unknown as IProject;
    return {
    // The name to show in the UI
    get displayName() {
      return self.model ? self.model.name : self.name;
    },
    // Convenience getter because we only support one model for now
    get model() {
      return self.models.length > 0 ? self.models[0] : null;
    },
    // get the active version for our (first) model
    get activeModelVersion() {
      return self.model !== null ? self.model.activeVersion : null;
    },
    // luxon DateTime object of the updatedAt property
    get updated() {
      return DateTime.fromISO(self.updatedAt);
    },
    // last edit = the maximum of our own updatedAt and the lastEdited of our model.
    get lastEdited() {
      // Legacy behaviour: `lastEdited` is only read for projects that have a
      // model, so `self.model` is non-null at the call sites. The hand interface
      // types it as nullable (a project *can* have no model); assert non-null
      // here to preserve byte-identical runtime without adding a guard.
      return DateTime.max(self.updated, self.model!.lastEdited);
    },
    // when was the last activity for this project
    get lastActivity() {
      // For now, we don't track views yet -> last activity = last edit
      return self.lastEdited;
    },
    // are we saving our project?
    get saveState() {
      return self.model !== null ? self.model.combinedSaveState : 'idle';
    },
    // convenience getter for the path of the project view for this project
    get path() {
      return `/project/${self.id}`;
    },
    };
  })
  .actions(self => ({
    // Retry saving
    retrySave() {
      if (self.model !== null) {
        return self.model.retrySave();
      } else {
        return Promise.resolve(true);
      }
    },
  }));

export default Project;
